// File handling for uploading pupil writing — shared by Upload (/upload) and the free sample (/try).

export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
// HEIC/HEIF from iPhone cameras — not supported by the AI pipeline; shown separately so
// teachers get a specific actionable message rather than a generic type error.
export const HEIC_TYPES = ['image/heic', 'image/heif'];
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export const HEIC_MESSAGE =
  'iPhone HEIC photos cannot be processed. On your iPhone go to Settings → Camera → Formats and choose "Most Compatible" to save photos as JPEG, then try again.';

// Infer MIME type from file extension when the browser reports an empty type.
// This happens on some Android/Chrome camera captures.
export function getMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const MAP: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg',
    png: 'image/png', webp: 'image/webp',
    pdf: 'application/pdf',
    heic: 'image/heic', heif: 'image/heif',
  };
  // Fall back to image/jpeg for files with no recognisable extension.
  // Android camera captures through the native picker often arrive with a blank
  // type AND a filename with no extension (e.g. "content" or "image").
  return MAP[ext] ?? 'image/jpeg';
}

// Derive a file extension from MIME type, used when the filename has no extension.
export function mimeToExt(mime: string): string {
  const MAP: Record<string, string> = {
    'image/jpeg': 'jpg', 'image/png': 'png',
    'image/webp': 'webp', 'application/pdf': 'pdf',
  };
  return MAP[mime] ?? 'jpg';
}

// Returns the message to show for a file that can't be used, or null if it's fine.
export function checkWritingFile(file: File, mime: string): string | null {
  if (HEIC_TYPES.includes(mime)) return HEIC_MESSAGE;
  if (!ACCEPTED_TYPES.includes(mime)) {
    return `${file.name || 'File'}: unsupported file type. Please use a JPG, PNG, WebP, or PDF.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `${file.name || 'File'}: file is too large. Maximum size is 20 MB.`;
  }
  return null;
}

// Read a file as a base64 data URL.
// Used instead of URL.createObjectURL() for thumbnails so image bytes are copied
// into JS memory immediately — avoids iOS releasing lazy-loaded camera file data
// when the file input is reset.
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

// IndexedDB helpers — persist File objects across the Android Chrome page reload
// that occurs when the native file picker opens as a separate Android activity.
// Unlike localStorage, IDB can store File/Blob objects natively and has no size cap.
// Each page passes its own database name so /upload and /try never share pending files.
const IDB_STORE = 'pending_files';

export interface IdbItem {
  id: string;
  file: File;
  mimeType: string;
  ts: number; // Date.now() — used to expire stale items
}

function idbOpen(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbSave(dbName: string, items: IdbItem[]): Promise<void> {
  const db = await idbOpen(dbName);
  const tx = db.transaction(IDB_STORE, 'readwrite');
  const store = tx.objectStore(IDB_STORE);
  for (const item of items) store.put(item);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function idbLoad(dbName: string): Promise<IdbItem[]> {
  const db = await idbOpen(dbName);
  const tx = db.transaction(IDB_STORE, 'readonly');
  const store = tx.objectStore(IDB_STORE);
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => { db.close(); resolve((req.result as IdbItem[]).sort((a, b) => a.ts - b.ts)); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function idbClear(dbName: string): Promise<void> {
  const db = await idbOpen(dbName);
  const tx = db.transaction(IDB_STORE, 'readwrite');
  tx.objectStore(IDB_STORE).clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

// Atomically replace all IDB records — used to keep IDB in sync with current pages.
export async function idbReplaceAll(dbName: string, items: IdbItem[]): Promise<void> {
  const db = await idbOpen(dbName);
  const tx = db.transaction(IDB_STORE, 'readwrite');
  const store = tx.objectStore(IDB_STORE);
  store.clear();
  for (const item of items) store.put(item);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}
